import { useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiUploadSimpleBold } from 'react-icons/pi';
import FileDropzone from '@v2/components/ui/FileDropzone';
import api from '@v2/lib/api';

type ImportResponse = {
    message: string;
    import: { containers: number; items: number; file_name: string };
    summary: { containers: number; items: number };
    urls: { conference: string; check: string };
};

type Props = {
    shipmentId: number;
    onSuccess?: () => void;
    size?: 'sm' | 'md';
    variant?: 'outline' | 'solid';
    className?: string;
    /** Apenas ícone na lista de tarefas */
    iconOnly?: boolean;
};

export default function ConferenceSheetImportButton({
    shipmentId,
    onSuccess,
    size = 'sm',
    variant = 'outline',
    className,
    iconOnly = false,
}: Props) {
    const inputRef = useRef<HTMLInputElement>(null);

    const mutation = useMutation({
        mutationFn: async (file: File) => {
            const form = new FormData();
            form.append('file', file);
            const { data } = await api.post<ImportResponse>(
                `/shipments/${shipmentId}/checks/import`,
                form,
            );
            return data;
        },
        onSuccess: (data) => {
            toast.success(
                `${data.import.containers} container(s), ${data.import.items} item(ns) importados.`,
            );
            onSuccess?.();
        },
        onError: () => {
            toast.error('Falha ao importar planilha Details.');
        },
    });

    const accept =
        '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

    if (!iconOnly) {
        return (
            <FileDropzone
                accept={accept}
                maxSizeMb={20}
                loading={mutation.isPending}
                disabled={mutation.isPending}
                label="Arraste a planilha Details"
                hint="Excel .xlsx / .xls · clique para selecionar"
                showSelected={false}
                className={className}
                onFiles={(files) => {
                    if (files[0]) mutation.mutate(files[0]);
                }}
                onError={(msg) => toast.error(msg)}
            />
        );
    }

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="hidden"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                        mutation.mutate(file);
                    }
                    e.target.value = '';
                }}
            />
            <button
                type="button"
                title="Importar planilha Details"
                disabled={mutation.isPending}
                className={
                    className ??
                    'rounded p-1 text-teal-700 hover:bg-teal-50 disabled:opacity-50'
                }
                onClick={() => inputRef.current?.click()}
            >
                <PiUploadSimpleBold className="h-4 w-4" />
            </button>
        </>
    );
}
